import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface PinnedTip {
  id: string;
  tip_id: string;
  pinned_at: string;
  tip: {
    id: string;
    title: string;
    content: string;
    category: string;
    image_url: string | null;
    youtube_url: string | null;
    rating: number;
    rating_count: number;
    created_at: string;
    published_at: string | null;
  };
}

export const usePinnedTips = () => {
  const [pinnedTips, setPinnedTips] = useState<PinnedTip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchPinnedTips = async () => {
    if (!user) {
      setPinnedTips([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("pinned_tips")
        .select(`
          id,
          tip_id,
          pinned_at,
          tip:tips (
            id,
            title,
            content,
            category,
            image_url,
            youtube_url,
            rating,
            rating_count,
            created_at,
            published_at
          )
        `)
        .eq("user_id", user.id)
        .order("pinned_at", { ascending: false });

      if (error) throw error;

      setPinnedTips(data || []);
    } catch (err) {
      console.error("Error fetching pinned tips:", err);
      setError("Failed to load pinned tips");
    } finally {
      setIsLoading(false);
    }
  };

  const pinTip = async (tipId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to pin tips",
        variant: "destructive",
      });
      return { success: false };
    }

    try {
      const { error } = await supabase
        .from("pinned_tips")
        .insert([{ user_id: user.id, tip_id: tipId }]);

      if (error) {
        if (error.code === "23505") { // Unique constraint violation
          toast({
            title: "Already Pinned",
            description: "This tip is already in your favorites",
            variant: "destructive",
          });
          return { success: false };
        }
        throw error;
      }

      toast({
        title: "Tip Pinned!",
        description: "Added to your favorites",
      });

      fetchPinnedTips(); // Refresh the list
      return { success: true };
    } catch (err) {
      console.error("Error pinning tip:", err);
      toast({
        title: "Error",
        description: "Failed to pin tip",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const unpinTip = async (tipId: string) => {
    if (!user) return { success: false };

    try {
      const { error } = await supabase
        .from("pinned_tips")
        .delete()
        .eq("user_id", user.id)
        .eq("tip_id", tipId);

      if (error) throw error;

      toast({
        title: "Tip Unpinned",
        description: "Removed from your favorites",
      });

      fetchPinnedTips(); // Refresh the list
      return { success: true };
    } catch (err) {
      console.error("Error unpinning tip:", err);
      toast({
        title: "Error",
        description: "Failed to unpin tip",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const isPinned = (tipId: string) => {
    return pinnedTips.some(pinned => pinned.tip_id === tipId);
  };

  useEffect(() => {
    fetchPinnedTips();

    // Set up real-time subscription for pinned tips updates
    if (user) {
      const channel = supabase
        .channel('pinned-tips-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'pinned_tips',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Pinned tips real-time update:', payload);
            fetchPinnedTips();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  return {
    pinnedTips,
    isLoading,
    error,
    pinTip,
    unpinTip,
    isPinned,
    refetch: fetchPinnedTips
  };
};